/**
 * Date Range Selector Component
 *
 * Dropdown for selecting predefined date ranges or custom date range.
 * Used across all KPI pages for consistent date filtering.
 *
 * Preset options:
 * - Last 12 Months, Last 24 Months
 * - Year to Date, Last Year
 * - Custom (shows date pickers)
 *
 * Created: 2026-03-28
 * Part of KPI V1 Implementation
 */

import { useTranslation } from "react-i18next";
import { useState } from "react";

interface DateRangeSelectorProps {
    value: string;
    onChange: (value: string) => void;
    customRange: { start: string; end: string };
    onCustomRangeChange: (range: { start: string; end: string }) => void;
    options?: { value: string; label: string }[];
}

export default function DateRangeSelector({
    value,
    onChange,
    customRange,
    onCustomRangeChange,
    options,
}: DateRangeSelectorProps) {
    const { t } = useTranslation();
    const [showCustom, setShowCustom] = useState(value === "custom");

    // Generate dynamic year options from 2024 to last year
    const currentYear = new Date().getFullYear();
    const lastYear = currentYear - 1;
    const startYear = 2024;

    // Build default options array
    const defaultOptions: { value: string; label: string }[] = [
        { value: "ytd", label: t("kpi.ytd") },
        { value: "rolling_2_years", label: t("kpi.rolling_2_years") },
    ];

    // Add individual year options (2024, 2025, ... up to lastYear)
    for (let year = startYear; year <= lastYear; year++) {
        defaultOptions.push({ value: `year_${year}`, label: String(year) });
    }

    // Add remaining options
    defaultOptions.push(
        { value: "last_12_months", label: t("kpi.last_12_months") },
        { value: "custom", label: t("kpi.custom") },
    );

    const handleChange = (newValue: string) => {
        onChange(newValue);
        setShowCustom(newValue === "custom");
    };

    return (
        <div className="flex items-center gap-2">
            {/* Preset dropdown */}
            <select
                value={value}
                onChange={(e) => handleChange(e.target.value)}
                className="rounded border border-stroke bg-white py-1.5 px-3 text-sm font-medium outline-none transition focus:border-primary dark:border-strokedark dark:bg-boxdark"
            >
                {(options || defaultOptions).map((option) => (
                    <option key={option.value} value={option.value}>
                        {option.label}
                    </option>
                ))}
            </select>

            {/* Custom date range inputs */}
            {showCustom && (
                <div className="flex items-center gap-2">
                    <input
                        type="date"
                        value={customRange.start}
                        onChange={(e) =>
                            onCustomRangeChange({ ...customRange, start: e.target.value })
                        }
                        className="rounded border border-stroke bg-white py-1.5 px-2 text-sm outline-none transition focus:border-primary dark:border-strokedark dark:bg-boxdark"
                    />
                    <span className="text-sm">-</span>
                    <input
                        type="date"
                        value={customRange.end}
                        onChange={(e) =>
                            onCustomRangeChange({ ...customRange, end: e.target.value })
                        }
                        className="rounded border border-stroke bg-white py-1.5 px-2 text-sm outline-none transition focus:border-primary dark:border-strokedark dark:bg-boxdark"
                    />
                </div>
            )}
        </div>
    );
}
